import { useState, useEffect } from "react";
import { toast } from "sonner";
import { useT } from "@/lib/i18n";
import { api, errMsg, fmtDate, fmtTime, fmtMin, todayStr, addDays } from "@/lib/api";
import { Card, Loader, PageTitle, GeoBadge, CorrectedNote } from "@/components/kv";

export const Kpi = ({ label, value, accent = "", testid }) => (
  <Card className="p-4" data-testid={testid}>
    <div className="text-xs text-stone-500">{label}</div>
    <div className={`font-mono-kv text-xl sm:text-2xl font-bold mt-1 ${accent}`}>{value}</div>
  </Card>
);

export default function StaffHistory() {
  const { t, lang } = useT();
  const [data, setData] = useState(null);

  useEffect(() => {
    const end = todayStr();
    api.get("/me/attendance", { params: { start: addDays(end, -30), end } }).then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e, t)));
  }, [t]);

  if (!data) return <Loader />;
  const tot = data.totals;
  return (
    <div>
      <PageTitle kicker={t("staffnav.history")} title={t("history.title")} />
      <div className="grid grid-cols-2 gap-3 mb-5">
        <Kpi label={t("history.worked")} value={fmtMin(tot.worked_min, t)} testid="history-total-worked" />
        <Kpi label={t("history.normal")} value={fmtMin(tot.normal_min, t)} testid="history-total-normal" />
        <Kpi label={t("history.overtime")} value={fmtMin(tot.overtime_min, t)} accent="text-orange-700" testid="history-total-overtime" />
        <Kpi label={t("history.late")} value={tot.late_count} accent="text-red-700" testid="history-late-count" />
      </div>
      <div className="space-y-2" data-testid="history-list">
        {data.items.length === 0 && <p className="text-stone-500 text-sm">{t("history.none")}</p>}
        {[...data.items].reverse().map((r) => (
          <Card key={r.id} className="p-4 space-y-2">
            <div className="flex items-center justify-between gap-2">
              <span className="font-semibold capitalize">{fmtDate(r.date, lang)}</span>
              <span className="font-mono-kv text-sm">{fmtTime(r.clock_in)} – {r.open ? t("report.open") : fmtTime(r.clock_out)}</span>
            </div>
            <div className="flex flex-wrap gap-2 items-center text-xs">
              <GeoBadge verified={r.in_verified} distance={r.in_distance_m} manual={r.manual} />
              <span className="text-stone-600">{t("report.worked")}: {fmtMin(r.worked_min, t)}</span>
              {r.overtime_min > 0 && <span className="rounded-full bg-orange-100 text-orange-800 px-2 py-0.5 font-semibold">{t("report.ot_badge")} +{fmtMin(r.overtime_min, t)}</span>}
              {r.is_late && <span className="rounded-full bg-amber-100 text-amber-900 px-2 py-0.5 font-semibold">{t("dash.late_by", { m: fmtMin(r.late_min, t) })}</span>}
            </div>
            <CorrectedNote r={r} />
          </Card>
        ))}
      </div>
    </div>
  );
}
